import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { CheckCircle2, X, Send, PenLine, FileSignature, Printer, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/shared";
import { SendConsentDialog } from "@/components/HipaaDisclosure";

const Row = ({ ok, title, sub }) => (
  <div className="flex items-center gap-3">
    {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <X className="w-5 h-5 text-destructive" />}
    <div><p className="font-medium text-sm">{title}</p><p className="text-xs text-muted-foreground">{sub}</p></div>
  </div>
);

export const ConsentTab = ({ patient, onChanged }) => {
  const [forms, setForms] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [name, setName] = useState('');
  const [hipaa, setHipaa] = useState(false);
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => { axios.get(`${API}/patients/${patient.id}/consent-forms`).then(r => setForms(r.data)).catch(() => {}); }, [patient.id]);
  useEffect(() => { load(); }, [load]);

  const signInPerson = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/patients/${patient.id}/consent-forms/in-person`, { hipaa_acknowledged: hipaa, treatment_consent: consent, signature_name: name.trim(), signer_relationship: 'self' });
      toast.success('Consent recorded on chart'); setSignOpen(false); setName(''); setHipaa(false); setConsent(false); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openPdf = async (f, download) => {
    try {
      const r = await axios.get(`${API}/patients/${patient.id}/consent-forms/${f.id}/pdf`, { params: { download }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (download) { const a = document.createElement('a'); a.href = url; a.download = `EliteIV-Consent-${patient.last_name}-${f.signed_at.slice(0, 10)}.pdf`; a.click(); }
      else { const w = window.open(url, '_blank'); if (w) w.addEventListener('load', () => w.print()); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not generate PDF'); }
  };

  return (
    <div className="space-y-3" data-testid="chart-consent-tab">
      <Card className="bg-muted/20 border-border/40">
        <CardContent className="p-4 space-y-3">
          <Row ok={patient.hipaa_acknowledged} title="HIPAA Privacy Notice" sub={patient.hipaa_acknowledged ? 'Acknowledged' : 'Not acknowledged'} />
          <Row ok={patient.treatment_consent} title="Consent for Treatment" sub={patient.treatment_consent ? 'Signed' : 'Not signed'} />
          {patient.consent_signed_at && <p className="text-xs text-primary">✓ Last signed: {format(parseISO(patient.consent_signed_at), 'MMM d, yyyy h:mm a')}</p>}
          {patient.consent_sent_at && !patient.consent_signed_at && <p className="text-xs text-amber-300">Sent for e-signature {format(parseISO(patient.consent_sent_at), 'MMM d, h:mm a')} · awaiting patient</p>}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" onClick={() => setSendOpen(true)} disabled={!patient.email} className="h-8 text-xs bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold" data-testid="chart-consent-send"><Send className="w-3.5 h-3.5 mr-1" />Email to sign in advance</Button>
            <Button size="sm" variant="outline" onClick={() => setSignOpen(true)} className="h-8 text-xs border-primary/40 text-primary" data-testid="chart-consent-sign-now"><PenLine className="w-3.5 h-3.5 mr-1" />Sign now (in person)</Button>
          </div>
        </CardContent>
      </Card>

      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Signed forms on file ({forms.length})</p>
        {forms.length === 0 && <p className="text-xs text-muted-foreground">No signed forms yet.</p>}
        <div className="space-y-1.5">
          {forms.map(f => (
            <div key={f.id} className="rounded-lg border border-emerald-500/30 bg-emerald-950/10 p-3 flex items-start gap-3" data-testid={`consent-form-${f.id}`}>
              <FileSignature className="w-4 h-4 text-emerald-300 mt-0.5" />
              <div className="text-xs flex-1 min-w-0">
                <p className="font-semibold text-sm">{f.forms.join(' + ')}</p>
                <p className="text-muted-foreground">Signed by <span className="italic font-serif text-foreground">{f.signature_name}</span>{f.signer_relationship && f.signer_relationship !== 'self' ? ` (${f.signer_relationship.replace('_', ' / ')})` : ''} · {format(parseISO(f.signed_at), 'MMM d, yyyy h:mm a')} · {f.method === 'in_person' ? `in person, witnessed by ${f.witnessed_by}` : 'electronic'}{f.ip ? ` · IP ${f.ip}` : ''}</p>
                {f.document_version && <p className="text-muted-foreground/70">Document v{f.document_version}</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 border-primary/40 text-primary" onClick={() => openPdf(f, false)} data-testid={`consent-print-${f.id}`}><Printer className="w-3 h-3 mr-1" />Print</Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2" onClick={() => openPdf(f, true)} data-testid={`consent-download-${f.id}`}><Download className="w-3 h-3 mr-1" />PDF</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <SendConsentDialog open={sendOpen} onOpenChange={setSendOpen} patients={[patient]} presetPatientId={patient.id} />
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent data-testid="consent-inperson-dialog">
          <DialogHeader><DialogTitle>Sign in person</DialogTitle><DialogDescription>Read the HIPAA notice and Consent for Treatment with the patient, then have them type their name.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <label className="flex items-start gap-2 text-sm cursor-pointer"><Checkbox checked={hipaa} onCheckedChange={v => setHipaa(!!v)} data-testid="inperson-hipaa" className="mt-0.5" />Patient received and understands the HIPAA Privacy Notice & Patient Rights</label>
            <label className="flex items-start gap-2 text-sm cursor-pointer"><Checkbox checked={consent} onCheckedChange={v => setConsent(!!v)} data-testid="inperson-consent" className="mt-0.5" />Patient understands the risks (incl. infection) and consents to IV/IM treatment</label>
            <div className="space-y-1"><Label>Patient's full name (signature)</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder={`${patient.first_name} ${patient.last_name}`} className="font-serif italic" data-testid="inperson-name" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)}>Cancel</Button>
            <Button onClick={signInPerson} disabled={saving || !hipaa || !consent || name.trim().length < 2} className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold" data-testid="inperson-submit">{saving ? 'Saving…' : 'Record signature'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
